import * as React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAppSelector } from '../../../app/hooks';
import { useLoadLeagueData, useAddUpdatePlayerClaim } from '../../../app/dataLoaderHooks';
import { League, Team, TradeProposalPlayerActionEnum, TradeProposalPlayer, WaiverClaim, TeamPlayerClaims, RosterPositionEnum } from '../../../sdk/model';
import {Box, Typography, Button} from '@mui/material';
import TradeTeamViewer from '../trade/TradeTeamViewer';
import TradeDetails from '../trade/TradeDetails';
import { getNewSalary, getSalaryForSeason } from '../../util/SalaryDisplay';
import PlayerCardLink from '../../cards/playerCardLink';
import { playerClaimApi } from '../../../adapters/APIExporter';
import { getRosterSize, getMaxTeamSize } from '../../util/TeamUtils';

function DropForWaiverClaim() {
  const navigate = useNavigate();
  const {leagueId, teamId, playerId} = useParams();
  
  const [isException, setIsException] = React.useState(false);
  const [isLoadComplete, setLoadComplete] = React.useState(false);
  const [dropPlayerIds, setDropPlayerIds] = React.useState<string[]>([]);
  const [navigateAway, setNavigateAway] = React.useState(false);
  const [newTeamPlayerClaims, setNewTeamPlayerClaims] = React.useState<TeamPlayerClaims | undefined>(undefined);
  const [errors, setErrors] = React.useState<Array<string>>([]);
  
  const user = useAppSelector((state) => state.user);
  
  const league : League|undefined = useLoadLeagueData({leagueId: leagueId, userId: user.id, loadBasicDraftData: true,
                                                            loadFullDraftData: false, loadContractData: true,
                                                            loadBasicAuctionData: false, loadFullAuctionData: false,
                                                            loadRosterData: false, loadRosterFor: [teamId!], loadWaivedContracts: true,
                                                            isException: isException, setIsException: setIsException,
                                                            isLoadComplete: isLoadComplete, setLoadComplete: setLoadComplete});
  
  const teamMap : Map<string | undefined, Team> = new Map(league?.teams?.map((team) => [team.id, team]));                                           
  const myTeam = teamMap.get(teamId);
  
  const playerDataLoad = useAppSelector((state) => state.playerDataLoad );
  const playerMap = new Map(playerDataLoad.flatMap(team => team.players).map((player) => [player?.id, player]));
  
  useAddUpdatePlayerClaim(teamId!, newTeamPlayerClaims?.id, newTeamPlayerClaims);
  if (newTeamPlayerClaims) {
    setNewTeamPlayerClaims(undefined);
  }
  
  React.useEffect(() => {
    if (navigateAway) {
      navigate('/league/' + leagueId);
    }
  }, [navigateAway]);
  
  function handleDropChange(playerId: string, fromTeamId: string, action: string) {
    const newDropPlayerIds = [...dropPlayerIds];
    if (action === 'drop') {
      newDropPlayerIds.push(playerId);
    } else {
      const playerIndex = dropPlayerIds.indexOf(playerId);
      newDropPlayerIds.splice(playerIndex, 1);
    }
    setDropPlayerIds(newDropPlayerIds);
  }
  
  function setErrorsAndFocus(errors) {
    setErrors(errors);
    document.getElementById("errors")!.focus();
  }
  
  function makeWaiverClaim() {
    const claim : WaiverClaim = {
      type: 'waiver_claim',
      playerId: playerId,
      dropPlayerIds: dropPlayerIds
    }
    playerClaimApi.claimWaivedContract(claim, teamId).then(response => {
      setNewTeamPlayerClaims(response.data);
      setNavigateAway(true);
    }).catch(error => {
      let errors = [error.response.data.message];
      setErrorsAndFocus(errors);
    });
  }
  
  if (!isLoadComplete || !myTeam?.rosterLoaded || !leagueId || !teamId || !playerId) {
    return <p>Loading...</p>
  }
  
  const players : TradeProposalPlayer[] = dropPlayerIds.map(dropPlayerId => {
    return {
      playerId: dropPlayerId,
      fromTeamId: teamId,
      action: TradeProposalPlayerActionEnum.Drop
    }
  })
  
  players.push({
    playerId: playerId,
    toTeamId: teamId,
    action: TradeProposalPlayerActionEnum.Trade
  });
  
  const proposalPlayerMap = new Map(players.map(player => [player.playerId, player]));
  const teamPlayerMap = new Map(Object.values(myTeam.playerRoster!).flatMap(playerList => 
    playerList.filter(player => player).map(player => [player.playerId, player])
  ));
  const thisContract = league!.waivedContracts!.find(contract => contract.playerId === playerId);
  teamPlayerMap.set(playerId, {
    playerId: playerId,
    contract: thisContract
  });
  
  const rosterSize = getRosterSize(myTeam);
  const newRosterSize = players.reduce((partialSum, tradePlayer) => {
    const owningTeam = teamMap.get(tradePlayer.fromTeamId);
    const ir = owningTeam?.playerRoster![RosterPositionEnum.Ir]
    const isIr = ir && ir.some(player => player && player.playerId === tradePlayer.playerId);
    return partialSum + (tradePlayer.toTeamId === myTeam?.id ? 1 : (tradePlayer.fromTeamId === myTeam?.id && !isIr ? -1 : 0));
  }, rosterSize);
  const maxRosterSize = getMaxTeamSize(league!);
  const newEmptyRosterSpots = maxRosterSize - newRosterSize;
  const overRosterLimit = newEmptyRosterSpots < 0;
  const currentSalary = getSalaryForSeason(myTeam!.paymentsBySeason![league?.currentSeasonYear!]);
  const newSalary = getNewSalary(myTeam!, teamPlayerMap, new Map(), currentSalary, players, [], league?.currentSeasonYear!, 0, newEmptyRosterSpots, league?.currentSeason)
  const overCap = newSalary > league?.currentSeason?.nflSeason?.salaryCap!;
  const player = playerMap.get(playerId)!;
  
  let errorInfo = errors.map((error, index) => {
    return (<p className='error' key={'error_' + index}>{error}</p>);
  });
  return <Box>
    <p>You are about to make a waiver claim for <PlayerCardLink player={player} leagueId={leagueId!} />.  Please select below if you would like to conditionally drop player(s) as a part of this claim.</p>
    <Box tabIndex="0" id="errors">{errorInfo}</Box>
    <TradeTeamViewer team={myTeam} currentSeason={league?.currentSeason?.seasonYear!}
            playerMap={playerMap} teamMap={new Map([[teamId, myTeam]])} userId={user?.id} myTeam={true} 
            otherTeamIds={[]} handleTradeProposalChange={handleDropChange}
            proposalPlayerMap={proposalPlayerMap} proposalPickMap={undefined} earliestLockDate={thisContract?.waiverExpirationDate ? new Date(thisContract?.waiverExpirationDate) : null} />
    <Typography variant="h6" component="div" color="text.secondary" sx={{ flexGrow: 1 }}>
      Full Details
    </Typography>
    <TradeDetails leagueId={leagueId} teams={[myTeam]} currentSeason={league?.currentSeasonYear} players={players} picks={[]} playerMap={playerMap} teamMap={teamMap} teamPlayerMap={teamPlayerMap} pickMap={new Map()} salaryCap={league!.currentSeason!.nflSeason!.salaryCap!} maxRosterSize={maxRosterSize} />
    <Button color="primary" variant="contained" onClick={makeWaiverClaim} disabled={overCap || overRosterLimit}>Complete Claim</Button>
  </Box>
}

export default DropForWaiverClaim;